import { ApolloProvider } from '@apollo/client';
import type { Country } from '@seek/audience-zones';
import { MelwaysProvider } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import { VocabProvider } from '@vocab/react';
import { BraidProvider, ToastProvider } from 'braid-design-system';
import seekJobs from 'braid-design-system/themes/seekJobs';
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

import { MOCK_API_URL, SEEK_ENV } from 'src/config';
import { createAnalytics, defaultState } from 'src/modules/analytics';
import { apiClient } from 'src/modules/api-client';
// @ts-expect-error
import { deepLinks } from 'src/modules/deep-links';
import { detectUnsavedChanges } from 'src/modules/detect-unsaved-changes';

// @ts-expect-error
import createStore from './createStore';
import { App } from './handlers/App';
import { AnalyticsProvider } from './modules/analytics/AnalyticsProvider';
import type { AnalyticsBrand } from './modules/analytics/types';
import { AppConfigProvider } from './modules/appConfig/AppConfigProvider';
import { RetryActionProvider } from './modules/retry-actions/RetryActionProvider';
import { getQueryParams } from './modules/utils';
import type { ClientContext } from './render';
import { siteNameToCountryMap } from './siteNameMaps';

export default ({
  site,
  language,
  vocabLanguage,
  locale,
  brand,
  zone,
}: ClientContext) => {
  const analytics = createAnalytics({
    ...defaultState,
    siteCountry: siteNameToCountryMap[site].toLowerCase() as Lowercase<Country>,
    brand: brand.toLowerCase() as AnalyticsBrand,
    zone,
    siteLanguage: language,
  });

  const client = apiClient({
    seekEnv: SEEK_ENV,
    mockApi: MOCK_API_URL,
    language,
    zone,
    site,
  });

  const appConfigParam = {
    site,
    zone,
    brand,
    locale,
    language,
  };

  const store = createStore(analytics, undefined, client, appConfigParam);

  const { pseudoLocalization = '' } = getQueryParams(window.location.href);

  const { getState } = store;

  detectUnsavedChanges(store);
  deepLinks(window.location.pathname);

  metrics.addGlobalTags([`page-name:${getState().router.page}`]);
  metrics.addGlobalTags([`country:${locale}`, `language:${language}`]);

  const container = document.getElementById('app');

  hydrateRoot(
    container!,
    <Provider store={store}>
      <AnalyticsProvider value={analytics}>
        <RetryActionProvider value={{}}>
          <ApolloProvider client={client.apolloClient}>
            <VocabProvider
              language={
                pseudoLocalization && SEEK_ENV !== 'prod'
                  ? 'en-PSEUDO'
                  : vocabLanguage
              }
              {...(vocabLanguage === 'th-TH'
                ? { locale: 'th-u-ca-gregory' }
                : {})}
            >
              <MelwaysProvider.Client site={site}>
                <BraidProvider theme={seekJobs}>
                  <AppConfigProvider param={appConfigParam}>
                    <ToastProvider>
                      <App />
                    </ToastProvider>
                  </AppConfigProvider>
                </BraidProvider>
              </MelwaysProvider.Client>
            </VocabProvider>
          </ApolloProvider>
        </RetryActionProvider>
      </AnalyticsProvider>
    </Provider>,
  );
};
